import {
    AutoAwesome as AutoAwesomeIcon,
    Hub as HubIcon,
} from "@mui/icons-material";
import { Box, Chip, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { usePagesTranslation } from "../../locales";

/** Landing "connections" editorial carousel — three slides. */
const CONNECTIONS_CAROUSEL_INTERVAL_MS = 10_000;
const CONNECTIONS_SLIDE_COUNT = 3;
const CONNECTIONS_SLIDE_WIDTH_PCT = 100 / CONNECTIONS_SLIDE_COUNT;

const DIFFICULTY_CHIP_SX = {
    height: 28,
    fontWeight: 700,
    fontSize: "0.85rem",
    "& .MuiChip-label": { px: 1.25 },
};

const SLIDE_SX = {
    width: `${CONNECTIONS_SLIDE_WIDTH_PCT}%`,
    flexShrink: 0,
    boxSizing: "border-box",
    p: { xs: 3, sm: 4, md: 5 },
    minHeight: { xs: 320, sm: 280, md: 260 },
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
};

const GREEN_BAR_MOBILE_SX = {
    display: { xs: "block", sm: "none" },
    height: 4,
    width: 96,
    borderRadius: 2,
    background: "linear-gradient(90deg, #3BA666, #6bcf8e)",
    mb: 2.5,
    flexShrink: 0,
    alignSelf: "center",
};

const GREEN_BAR_DESKTOP_SX = {
    display: { xs: "none", sm: "block" },
    position: "absolute",
    left: 0,
    top: 24,
    bottom: 24,
    width: 5,
    borderRadius: "0 6px 6px 0",
    background: "linear-gradient(180deg, #3BA666, #56c97f)",
};

const SLIDE_CONTENT_SX = {
    pl: { xs: 0, sm: 2.5 },
    position: "relative",
    width: "100%",
};

const SLIDE_TITLE_SX = {
    textAlign: "center",
    mb: 2.5,
    fontWeight: 800,
    fontSize: { xs: "1.55rem", sm: "1.95rem", md: "2.15rem" },
    lineHeight: 1.3,
    letterSpacing: "-0.02em",
    maxWidth: 800,
    mx: "auto",
    background: "linear-gradient(118deg, #1e3a2f 0%, #2e7d32 42%, #3BA666 100%)",
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    color: "transparent",
};

const SLIDE_BODY_SX = {
    textAlign: "center",
    maxWidth: 720,
    mx: "auto",
    lineHeight: 1.85,
    fontSize: { xs: "1.125rem", sm: "1.2rem", md: "1.28rem" },
    color: "rgba(45, 52, 54, 0.88)",
};

const AI_CHIP_SX = {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.65,
    verticalAlign: "middle",
    mx: { xs: 0.35, sm: 0.5 },
    px: { xs: 1, sm: 1.25 },
    py: 0.4,
    borderRadius: 2,
    background:
        "linear-gradient(180deg, rgba(59, 166, 102, 0.16) 0%, rgba(59, 166, 102, 0.09) 100%)",
    border: "1px solid rgba(59, 166, 102, 0.42)",
    boxShadow: "0 2px 10px rgba(59, 166, 102, 0.14)",
    color: "#14532d",
    fontWeight: 700,
    fontSize: "0.92em",
    lineHeight: 1.25,
};

function ConnectionsSlide({ children }) {
    return (
        <Box sx={SLIDE_SX}>
            <Box sx={GREEN_BAR_MOBILE_SX} aria-hidden />
            <Box sx={GREEN_BAR_DESKTOP_SX} aria-hidden />
            <Box sx={SLIDE_CONTENT_SX}>{children}</Box>
        </Box>
    );
}

export default function LandingConnectionsCarousel() {
    const { t } = usePagesTranslation();
    const [connectionsSlide, setConnectionsSlide] = useState(0);

    useEffect(() => {
        const id = window.setInterval(() => {
            setConnectionsSlide((s) => (s + 1) % CONNECTIONS_SLIDE_COUNT);
        }, CONNECTIONS_CAROUSEL_INTERVAL_MS);
        return () => window.clearInterval(id);
    }, []);

    return (
        <Box sx={{ mb: { xs: 4, sm: 6 }, maxWidth: 920, mx: "auto" }}>
            <Box
                sx={{
                    position: "relative",
                    borderRadius: 3,
                    background: "linear-gradient(145deg, rgba(255,255,255,0.58) 0%, rgba(246,252,249,0.52) 42%, rgba(238,248,242,0.48) 100%)",
                    backdropFilter: "blur(10px)",
                    boxShadow:
                        "0 12px 48px rgba(27, 94, 32, 0.08), 0 2px 12px rgba(0, 0, 0, 0.04)",
                    border: "1px solid rgba(255, 255, 255, 0.55)",
                    overflow: "hidden",
                }}
                role="region"
                aria-roledescription="carousel"
                aria-label={t("landing.connections-carousel-label")}
                aria-live="polite"
            >
                <HubIcon
                    sx={{
                        position: "absolute",
                        top: { xs: -16, sm: -12 },
                        right: { xs: -20, sm: -8 },
                        fontSize: { xs: 100, sm: 140 },
                        color: "rgba(59, 166, 102, 0.07)",
                        transform: "rotate(14deg)",
                        pointerEvents: "none",
                        zIndex: 0,
                    }}
                    aria-hidden
                />
                <Box sx={{ overflow: "hidden", position: "relative", zIndex: 1 }}>
                    <Box
                        sx={{
                            display: "flex",
                            width: `${CONNECTIONS_SLIDE_COUNT * 100}%`,
                            transition: "transform 0.65s cubic-bezier(0.4, 0, 0.2, 1)",
                            transform: `translateX(-${connectionsSlide * CONNECTIONS_SLIDE_WIDTH_PCT}%)`,
                        }}
                    >
                        <ConnectionsSlide>
                            <Typography variant="h4" component="h2" sx={SLIDE_TITLE_SX}>
                                {t("landing.connections-headline")}
                            </Typography>
                            <Typography variant="body1" component="div" sx={SLIDE_BODY_SX}>
                                <Box component="span">{t("landing.connections-body-before-ai")}</Box>
                                <Box component="span" sx={AI_CHIP_SX}>
                                    <AutoAwesomeIcon
                                        sx={{ fontSize: "1.2em", color: "#2e7d32", flexShrink: 0 }}
                                        aria-hidden
                                    />
                                    <Box component="span">{t("howto.tab-assistant")}</Box>
                                </Box>
                                <Box component="span">{t("landing.connections-body-after-ai")}</Box>
                            </Typography>
                        </ConnectionsSlide>

                        <ConnectionsSlide>
                            <Typography variant="h4" component="h2" sx={SLIDE_TITLE_SX}>
                                {t("landing.universe-title")}
                            </Typography>
                            <Typography variant="body1" component="div" sx={SLIDE_BODY_SX}>
                                <Box component="span">{t("landing.universe-body-before")} </Box>
                                <Box
                                    component="span"
                                    sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        flexWrap: "wrap",
                                        justifyContent: "center",
                                        gap: 0.5,
                                        verticalAlign: "middle",
                                        mx: 0.35,
                                    }}
                                >
                                    <Chip
                                        label={t("puzzle.card.difficulty-easy")}
                                        size="small"
                                        color="success"
                                        sx={DIFFICULTY_CHIP_SX}
                                    />
                                    <Box component="span" aria-hidden>,</Box>
                                    <Chip
                                        label={t("puzzle.card.difficulty-medium")}
                                        size="small"
                                        color="warning"
                                        sx={DIFFICULTY_CHIP_SX}
                                    />
                                    <Box component="span" sx={{ mx: 0.15 }}>
                                        {t("landing.universe-difficulty-or")}
                                    </Box>
                                    <Chip
                                        label={t("puzzle.card.difficulty-hard")}
                                        size="small"
                                        color="error"
                                        sx={DIFFICULTY_CHIP_SX}
                                    />
                                </Box>
                                <Box component="span"> {t("landing.universe-body-after")}</Box>
                            </Typography>
                        </ConnectionsSlide>

                        <ConnectionsSlide>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 1,
                                    flexWrap: "wrap",
                                    mb: 2.5,
                                    maxWidth: 800,
                                    mx: "auto",
                                }}
                            >
                                <Box
                                    component="span"
                                    aria-hidden
                                    sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                        fontSize: "1.75rem",
                                        lineHeight: 1,
                                    }}
                                >
                                    🎮
                                </Box>
                                <Typography
                                    variant="h4"
                                    component="h2"
                                    sx={{ ...SLIDE_TITLE_SX, mb: 0 }}
                                >
                                    {t("landing.featured-play-intro-title")}
                                </Typography>
                            </Box>
                            <Typography variant="body1" component="div" sx={SLIDE_BODY_SX}>
                                {t("landing.featured-play-intro-body")}
                            </Typography>
                        </ConnectionsSlide>
                    </Box>
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 1,
                        pb: 2,
                        pt: 0.5,
                        position: "relative",
                        zIndex: 1,
                    }}
                    aria-hidden
                >
                    {Array.from({ length: CONNECTIONS_SLIDE_COUNT }, (_, i) => i).map((i) => (
                        <Box
                            key={i}
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                bgcolor:
                                    connectionsSlide === i
                                        ? "#3BA666"
                                        : "rgba(59, 166, 102, 0.28)",
                                transition: "background-color 0.35s ease",
                            }}
                        />
                    ))}
                </Box>
            </Box>
        </Box>
    );
}
